import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { saveTokens, clearTokens, getAccessToken, apiFetch } from '../utils/api';

export const UserContext = createContext(null);

const STORAGE_KEY = 'prodia_user';

export function UserProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [authInitializing, setAuthInitializing] = useState(true);

  const setUser = useCallback((newUser, tokens) => {
    if (newUser) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newUser));
      if (tokens) saveTokens(tokens.access, tokens.refresh);
      setUserState(newUser);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      clearTokens();
      setUserState(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      const token = getAccessToken();
      const saved = localStorage.getItem(STORAGE_KEY);

      if (!token || !saved) {
        if (saved && !token) {
          localStorage.removeItem(STORAGE_KEY);
        }
        if (!cancelled) {
          setUserState(null);
          setAuthInitializing(false);
        }
        return;
      }

      let cachedUser = null;
      try {
        cachedUser = JSON.parse(saved);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        clearTokens();
        if (!cancelled) {
          setUserState(null);
          setAuthInitializing(false);
        }
        return;
      }

      if (!cancelled) {
        setUserState(cachedUser);
      }

      try {
        const res = await apiFetch('/auth/me/');
        if (!res.ok) {
          if (!cancelled) setUser(null);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        const merged = {
          ...cachedUser,
          ...data.user,
          permissions: data.permissions,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        setUserState(merged);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setAuthInitializing(false);
      }
    };

    initAuth();

    return () => {
      cancelled = true;
    };
  }, [setUser]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        authInitializing,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
